// Save as: src/pages/employee.jsx

import React, { useEffect, useState, useMemo } from "react";
import Pagination from '../components/Pagination';
import API from "../api";
import "./employee.css";
import Select from "react-select";

function Employees() {
  /* ===== State ===== */
  const [employees, setEmployees] = useState([]);
  const [services, setServices] = useState([]);
  const [error, setError] = useState(null);

  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);

  // wizard
  const [showModal, setShowModal] = useState(false);
  const [step, setStep] = useState(0);
  const [editingId, setEditingId] = useState(null);

  // Reset password modal
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetTarget, setResetTarget] = useState(null); // { _id, name }
  const [resetPw, setResetPw] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');

  // business schedule (constraints)
  const [businessSchedule, setBusinessSchedule] = useState({
    workingDays: [],
    workingHours: {},
  });
  const [workingHourErrors, setWorkingHourErrors] = useState({});
  const [breakHourErrors, setBreakHourErrors] = useState({});

  const daysOfWeek = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  const [search, setSearch] = useState('');

  const user = JSON.parse(localStorage.getItem("user"));
  const businessId = user?.user?.id || user?.id;

  const AVATAR_SVG = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='64' height='64' viewBox='0 0 64 64'%3E%3Crect width='64' height='64' rx='32' fill='%23ede9fe'/%3E%3Ccircle cx='32' cy='24' r='12' fill='%237c3aed'/%3E%3Cellipse cx='32' cy='52' rx='18' ry='13' fill='%237c3aed'/%3E%3C/svg%3E`;

  // image upload
  const [uploadingImg, setUploadingImg] = useState(false);
  const uploadEmployeeImage = async (file) => {
    if (!file) return;
    try {
      setUploadingImg(true);
      const fd = new FormData();
      fd.append("employeeImage", file);
      const resp = await API.post("/auth/upload/employee-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = resp.data?.url;
      if (url) {
        setFormState((p) => ({ ...p, imageUrl: url }));
        alert("Employee photo uploaded!");
      } else {
        alert("Upload failed: no URL returned");
      }
    } catch (e) {
      console.error(e);
      alert("Failed to upload photo");
    } finally {
      setUploadingImg(false);
    }
  };

  const [formState, setFormState] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    imageUrl: "",
    services_id: [],
    workingDays: [],
    workingHours: {},
    breaks: {},
    globalWorkingStart: "",
    globalWorkingEnd: "",
    globalBreakStart: "",
    globalBreakEnd: "",
  });

  /* ===== Effects ===== */
  useEffect(() => {
    const bootstrap = async () => {
      try {
        const [scheduleRes, empRes, servicesRes] = await Promise.all([
          API.get(`/auth/business-detail?businessId=${businessId}`),
          API.get(`/auth/employees/business/${businessId}`),
          API.get(`/auth/services`),
        ]);
        setBusinessSchedule({
          workingDays: scheduleRes.data.workingDays || [],
          workingHours: scheduleRes.data.workingHours || {},
        });
        setEmployees(empRes.data || []);
        setServices(
          (servicesRes.data || []).filter(
            (s) => s.business_id === businessId
          )
        );
      } catch (e) {
        // setError("Failed to load employees");
      }
    };
    if (businessId) bootstrap();
  }, [businessId]);

  /* ===== Helpers ===== */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const toggleDay = (day) => {
    setFormState((prev) => ({
      ...prev,
      workingDays: prev.workingDays.includes(day)
        ? prev.workingDays.filter((d) => d !== day)
        : [...prev.workingDays, day],
    }));
  };

  const updateHours = (day, field, value) => {
    const next = {
      ...(formState.workingHours[day] || {}),
      [field === "start" ? "opensAt" : "closesAt"]: value,
    };
    const bh = businessSchedule.workingHours[day];
    let err = "";
    if (bh) {
      const { opensAt, closesAt } = next;
      if (
        opensAt &&
        closesAt &&
        (opensAt < bh.opensAt || closesAt > bh.closesAt)
      ) {
        err = `Must be between ${bh.opensAt} - ${bh.closesAt}`;
      }
    }
    setFormState((p) => ({
      ...p,
      workingHours: { ...p.workingHours, [day]: next },
    }));
    setWorkingHourErrors((p) => ({ ...p, [day]: err }));
  };

  const updateBreaks = (day, field, value) => {
    const existing = formState.breaks[day]?.[0] || {};
    const upd = { ...existing, [field]: value };
    const bh = businessSchedule.workingHours[day];
    let err = "";
    if (bh) {
      const { start, end } = upd;
      if (start && end && (start < bh.opensAt || end > bh.closesAt)) {
        err = `Break must be within ${bh.opensAt} - ${bh.closesAt}`;
      }
    }
    setFormState((p) => ({
      ...p,
      breaks: { ...p.breaks, [day]: [upd] },
    }));
    setBreakHourErrors((p) => ({ ...p, [day]: err }));
  };

  const applyWorkingHours = () => {
    const updated = {};
    const errors = {};
    formState.workingDays.forEach((day) => {
      const bh = businessSchedule.workingHours[day];
      if (!bh) return;
      const s = formState.globalWorkingStart;
      const e = formState.globalWorkingEnd;
      if (s < bh.opensAt || e > bh.closesAt)
        errors[day] = `Must be between ${bh.opensAt} - ${bh.closesAt}`;
      else updated[day] = { opensAt: s, closesAt: e };
    });
    setWorkingHourErrors(errors);
    if (!Object.keys(errors).length)
      setFormState((p) => ({ ...p, workingHours: updated }));
  };

  const applyBreaksToAll = () => {
    const updated = {};
    const errors = {};
    formState.workingDays.forEach((day) => {
      const bh = businessSchedule.workingHours[day];
      if (!bh) return;
      const s = formState.globalBreakStart;
      const e = formState.globalBreakEnd;
      if (s < bh.opensAt || e > bh.closesAt)
        errors[day] = `Break must be within ${bh.opensAt} - ${bh.closesAt}`;
      else updated[day] = [{ start: s, end: e }];
    });
    setBreakHourErrors(errors);
    if (!Object.keys(errors).length)
      setFormState((p) => ({ ...p, breaks: updated }));
  };

  /* ===== CRUD ===== */

  // Add Employee: we DON'T care about working days/hours here anymore
  const openAdd = async () => {
    try {
      const scheduleRes = await API.get(
        `/auth/business-detail?businessId=${businessId}`
      );
      const business = scheduleRes.data;

      setFormState({
        name: "",
        email: "",
        phone: "",
        password: "",
        imageUrl: "",
        services_id: [],
        // you can keep these empty; user will set them later in Edit
        workingDays: business.workingDays || [],
        workingHours: business.workingHours || {},
        breaks: {},
        globalWorkingStart: "",
        globalWorkingEnd: "",
        globalBreakStart: "",
        globalBreakEnd: "",
      });
    } catch (err) {
      console.error("Failed to load business details", err);
      setFormState({
        name: "",
        email: "",
        phone: "",
        password: "",
        imageUrl: "",
        services_id: [],
        workingDays: [],
        workingHours: {},
        breaks: {},
        globalWorkingStart: "",
        globalWorkingEnd: "",
        globalBreakStart: "",
        globalBreakEnd: "",
      });
    }

    setEditingId(null);
    setStep(0);
    setShowModal(true);
  };

  const openEdit = (emp) => {
    setEditingId(emp._id);
    setFormState({
      name: emp.name,
      email: emp.email,
      phone: emp.phone || "",
      password: "",
      imageUrl: emp.imageUrl || "",
      services_id: (emp.services_id || []).map((id) =>
        typeof id === "object" ? id._id : id
      ),
      workingDays: emp.workingDays || [],
      workingHours: emp.workingHours || {},
      breaks: emp.breaks || {},
      globalWorkingStart: "",
      globalWorkingEnd: "",
      globalBreakStart: "",
      globalBreakEnd: "",
    });
    setStep(0);
    setShowModal(true);
  };

  const saveEmployee = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let payload = {
        name: formState.name,
        email: formState.email,
        business_id: businessId,
        imageUrl: formState.imageUrl,
      };
      if (editingId) {
        payload = { ...formState, business_id: businessId };
        await API.put(`/auth/employees/${editingId}`, payload);
        alert("Employee updated");
      } else {
        await API.post(`/auth/employees`, payload);
        alert("Employee added. Login credentials sent via email.");
      }
      const refreshed = await API.get(
        `/auth/employees/business/${businessId}`
      );
      setEmployees(refreshed.data);
      setShowModal(false);
    } catch (err) {
      alert("Error saving employee");
    } finally {
      setLoading(false);
    }
  };

  const deleteEmployee = async (id) => {
    if (!window.confirm("Delete this employee?")) return;
    await API.delete(`/auth/employees/${id}`);
    setEmployees((prev) => prev.filter((e) => e._id !== id));
  };

  const toggleAll = (e) =>
    setSelected(e.target.checked ? employees.map((e) => e._id) : []);
  const toggleSelect = (id) =>
    setSelected((p) =>
      p.includes(id) ? p.filter((i) => i !== id) : [...p, id]
    );
  const bulkDelete = async () => {
    if (!window.confirm("Delete selected employees?")) return;
    await API.post("/auth/employees/bulk-delete", { ids: selected });
    setEmployees((prev) => prev.filter((e) => !selected.includes(e._id)));
    setSelected([]);
  };

  const openResetPassword = (emp) => {
    setResetTarget(emp);
    setResetPw('');
    setResetConfirm('');
    setResetError('');
    setShowResetModal(true);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (resetPw.length < 6) { setResetError('Password must be at least 6 characters.'); return; }
    if (resetPw !== resetConfirm) { setResetError('Passwords do not match.'); return; }
    setResetLoading(true);
    setResetError('');
    try {
      await API.put(`/auth/employees/${resetTarget._id}`, { password: resetPw });
      setShowResetModal(false);
      alert(`Password updated for ${resetTarget.name}`);
    } catch (err) {
      setResetError('Failed to update password. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  // Tabs: only show Working Days / Hours / Breaks when editing
  const tabs = editingId
    ? ["Details", "Working Days", "Working Hours", "Breaks"]
    : ["Details"];

  const filteredEmployees = useMemo(() => employees.filter(emp =>
    emp.name?.toLowerCase().includes(search.toLowerCase()) ||
    emp.email?.toLowerCase().includes(search.toLowerCase()) ||
    (emp.phone || '').includes(search)
  ), [employees, search]);

  const [empPage, setEmpPage]         = useState(1);
  const [empPageSize, setEmpPageSize] = useState(10);
  useEffect(() => setEmpPage(1), [search]);
  const paginatedEmployees = useMemo(() =>
    filteredEmployees.slice((empPage - 1) * empPageSize, empPage * empPageSize),
  [filteredEmployees, empPage, empPageSize]);

  return (
    <div className="employee-page">
      <div className="emp-header">
        <h1 className="emp-title">
          Employees <span className="emp-count">{employees.length}</span>
        </h1>
        <div className="emp-header-right">
          <input
            className="emp-search"
            type="text"
            placeholder="Search employees…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div className="emp-actions">
            <button className="emp-btn emp-btn-primary" onClick={openAdd}>
              + Add Employee
            </button>
            {selected.length > 0 && (
              <button
                className="emp-btn emp-btn-danger-outline"
                onClick={bulkDelete}
              >
                Bulk Delete
              </button>
            )}
          </div>
        </div>
      </div>

      {error && (
        <p
          style={{ color: "red", maxWidth: 1100, margin: "0 auto 8px" }}
        >
          {error}
        </p>
      )}

      <div className="emp-card">
        <div className="emp-table-scroll">
        <table className="emp-table" style={{tableLayout:'fixed'}}>
          <thead>
            <tr>
              <th style={{ width: 44 }}>
                <input type="checkbox" onChange={toggleAll} />
              </th>
              {/* <th style={{ width: 280 }}>ID</th> */}
              <th style={{ width: 78 }}>Photo</th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Services</th>
              <th style={{ width: 120 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredEmployees.length === 0 && (
              <tr>
                <td colSpan={8} className="emp-empty">
                  {search ? 'No employees match your search' : 'No employees'}
                </td>
              </tr>
            )}
            {paginatedEmployees.map((emp) => (
              <tr key={emp._id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selected.includes(emp._id)}
                    onChange={() => toggleSelect(emp._id)}
                  />
                </td>
                {/* <td
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {emp._id}
                </td> */}
                <td>
                  {emp.imageUrl ? (
                    <img
                      className="emp-avatar"
                      src={emp.imageUrl}
                      alt={emp.name}
                      onError={(e) => { e.target.onerror = null; e.target.src = AVATAR_SVG; }}
                    />
                  ) : (
                    <img
                      className="emp-avatar"
                      src={AVATAR_SVG}
                      alt={emp.name}
                    />
                  )}
                </td>
                <td>{emp.name}</td>
                <td>{emp.email}</td>
                <td>{emp.phone || "-"}</td>
                <td>
                  <div className="emp-service-tags">
                    {Array.isArray(emp.services_id)
                      ? emp.services_id
                          .map((s) => (typeof s === "object" ? s.name || s._id : s))
                          .filter(Boolean)
                          .map((name, i) => (
                            <span key={i} className="emp-service-tag">{name}</span>
                          ))
                      : null}
                  </div>
                </td>
                <td>
                  <div className="emp-row-actions">
                    <button
                      className="emp-icon-btn"
                      onClick={() => openEdit(emp)}
                      title="Edit"
                    >
                      ✎
                    </button>
                    <button
                      className="emp-icon-btn emp-icon-reset"
                      onClick={() => openResetPassword(emp)}
                      title="Reset Password"
                    >
                      🔑
                    </button>
                    <button
                      className="emp-icon-btn emp-icon-danger"
                      onClick={() => deleteEmployee(emp._id)}
                      title="Delete"
                    >
                      🗑
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>{/* end emp-table-scroll */}
        <Pagination
          page={empPage}
          totalPages={Math.max(1, Math.ceil(filteredEmployees.length / empPageSize))}
          totalItems={filteredEmployees.length}
          pageSize={empPageSize}
          onPageChange={setEmpPage}
          onPageSizeChange={(s) => { setEmpPageSize(s); setEmpPage(1); }}
        />
      </div>

      {/* ===== Reset Password Modal ===== */}
      {showResetModal && resetTarget && (
        <div className="emp-modal-overlay" onClick={() => setShowResetModal(false)}>
          <div className="emp-modal emp-modal--sm" onClick={(e) => e.stopPropagation()}>
            <div className="emp-modal-header">
              <h3 className="emp-modal-title">Reset Password</h3>
              <button className="emp-modal-close" onClick={() => setShowResetModal(false)}>×</button>
            </div>
            <p className="emp-reset-subtitle">Set a new password for <strong>{resetTarget.name}</strong></p>
            <form onSubmit={handleResetPassword}>
              <label className="emp-label">New Password</label>
              <input
                className="emp-input"
                type="password"
                placeholder="Min. 6 characters"
                value={resetPw}
                onChange={(e) => setResetPw(e.target.value)}
                required
              />
              <label className="emp-label">Confirm Password</label>
              <input
                className="emp-input"
                type="password"
                placeholder="Repeat password"
                value={resetConfirm}
                onChange={(e) => setResetConfirm(e.target.value)}
                required
              />
              {resetError && <p className="emp-reset-error">{resetError}</p>}
              <div className="emp-modal-actions" style={{ marginTop: 20 }}>
                <button type="button" className="emp-btn emp-btn-light" onClick={() => setShowResetModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="emp-btn emp-btn-primary" disabled={resetLoading}>
                  {resetLoading ? 'Saving...' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===== Modal (multi-step only for edit) ===== */}
      {showModal && (
        <div
          className="emp-modal-overlay"
          onClick={() => setShowModal(false)}
        >
          <div
            className="emp-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="emp-modal-header">
              <h3 className="emp-modal-title">
                {editingId ? "Edit Employee" : "Add New Employee"}
              </h3>
              <button
                className="emp-modal-close"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>

            {/* Tabs */}
            <div className="emp-tabs">
              {tabs.map((label, i) => (
                <button
                  key={label}
                  type="button"
                  className={`emp-tab ${
                    step === i ? "emp-active" : ""
                  }`}
                  onClick={() => setStep(i)}
                >
                  {label}
                </button>
              ))}
            </div>

            <form onSubmit={saveEmployee}>
              {/* Step 0: Details */}
              {step === 0 && (
                <>
                  <label className="emp-label">Name</label>
                  <input
                    className="emp-input"
                    name="name"
                    value={formState.name}
                    onChange={handleInputChange}
                    required
                  />

                  <label className="emp-label">Email</label>
                  <input
                    className="emp-input"
                    type="email"
                    name="email"
                    value={formState.email}
                    onChange={handleInputChange}
                    required
                  />

                  {editingId && (
                    <>
                      <label className="emp-label">Phone</label>
                      <input
                        className="emp-input"
                        name="phone"
                        value={formState.phone}
                        onChange={handleInputChange}
                      />
                    </>
                  )}

                  <label className="emp-label">Photo</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      uploadEmployeeImage(e.target.files?.[0])
                    }
                    disabled={uploadingImg}
                  />
                  {uploadingImg && (
                    <p className="emp-drop-note">Uploading...</p>
                  )}
                  {formState.imageUrl && (
                    <div style={{ marginTop: 8 }}>
                      <img
                        src={formState.imageUrl}
                        alt="Employee"
                        onError={(e) => { e.target.onerror = null; e.target.src = AVATAR_SVG; }}
                        style={{ maxWidth: 160, borderRadius: 8, display: "block" }}
                      />
                    </div>
                  )}

                  {editingId && (
                    <>
                      <label className="emp-label">Services</label>
                      <Select
                        isMulti
                        options={services.map((s) => ({
                          label: s.name,
                          value: s._id,
                        }))}
                        value={services
                          .filter((s) =>
                            formState.services_id.includes(s._id)
                          )
                          .map((s) => ({
                            label: s.name,
                            value: s._id,
                          }))}
                        onChange={(opts) =>
                          setFormState((p) => ({
                            ...p,
                            services_id: opts.map((o) => o.value),
                          }))
                        }
                      />
                    </>
                  )}
                </>
              )}

              {/* Step 1: Working Days — ONLY ON EDIT */}
              {editingId && step === 1 && (
                <>
                  <label className="emp-label">
                    <strong>Select Working Days <small>(Refresh if Working days are not visible)</small></strong>
                  </label>
                  <div className="emp-days">
                    {daysOfWeek
                      .filter((d) =>
                        businessSchedule.workingDays.includes(d)
                      )
                      .map((day) => (
                        <label key={day} className="emp-day">
                          <input
                            type="checkbox"
                            checked={formState.workingDays.includes(
                              day
                            )}
                            onChange={() => toggleDay(day)}
                          />
                          {day}
                        </label>
                      ))}
                  </div>
                </>
              )}

              {/* Step 2: Working Hours — ONLY ON EDIT */}
              {editingId && step === 2 && (
                <>
                  <label className="emp-label">
                    <strong>Set Working Hours</strong>
                  </label>
                  <div className="emp-time-toolbar">
                    <span>Global Start:</span>
                    <input
                      className="emp-input"
                      type="time"
                      value={formState.globalWorkingStart}
                      onChange={(e) =>
                        setFormState((p) => ({
                          ...p,
                          globalWorkingStart: e.target.value,
                        }))
                      }
                      style={{ width: 160 }}
                    />
                    <span>Global End:</span>
                    <input
                      className="emp-input"
                      type="time"
                      value={formState.globalWorkingEnd}
                      onChange={(e) =>
                        setFormState((p) => ({
                          ...p,
                          globalWorkingEnd: e.target.value,
                        }))
                      }
                      style={{ width: 160 }}
                    />
                    <button
                      type="button"
                      className="emp-btn emp-btn-light"
                      onClick={applyWorkingHours}
                    >
                      Apply To All
                    </button>
                  </div>

                  {formState.workingDays.map((day) => (
                    <div key={day} className="emp-time-row">
                      <span className="emp-dayname">{day}</span>
                      <input
                        className="emp-input"
                        type="time"
                        value={
                          formState.workingHours[day]?.opensAt || ""
                        }
                        onChange={(e) =>
                          updateHours(day, "start", e.target.value)
                        }
                        style={{ width: 160 }}
                      />
                      <input
                        className="emp-input"
                        type="time"
                        value={
                          formState.workingHours[day]?.closesAt || ""
                        }
                        onChange={(e) =>
                          updateHours(day, "end", e.target.value)
                        }
                        style={{ width: 160 }}
                      />
                      {workingHourErrors[day] && (
                        <span style={{ color: "#b91c1c" }}>
                          {workingHourErrors[day]}
                        </span>
                      )}
                    </div>
                  ))}
                </>
              )}

              {/* Step 3: Breaks — ONLY ON EDIT */}
              {editingId && step === 3 && (
                <>
                  <label className="emp-label">
                    <strong>Set Break Hours</strong>
                  </label>
                  <div className="emp-time-toolbar">
                    <span>Break Start:</span>
                    <input
                      className="emp-input"
                      type="time"
                      value={formState.globalBreakStart}
                      onChange={(e) =>
                        setFormState((p) => ({
                          ...p,
                          globalBreakStart: e.target.value,
                        }))
                      }
                      style={{ width: 160 }}
                    />
                    <span>Break End:</span>
                    <input
                      className="emp-input"
                      type="time"
                      value={formState.globalBreakEnd}
                      onChange={(e) =>
                        setFormState((p) => ({
                          ...p,
                          globalBreakEnd: e.target.value,
                        }))
                      }
                      style={{ width: 160 }}
                    />
                    <button
                      type="button"
                      className="emp-btn emp-btn-light"
                      onClick={applyBreaksToAll}
                    >
                      Apply To All
                    </button>
                  </div>

                  {formState.workingDays.map((day) => (
                    <div key={day} className="emp-time-row">
                      <span className="emp-dayname">{day}</span>
                      <input
                        className="emp-input"
                        type="time"
                        value={
                          formState.breaks[day]?.[0]?.start || ""
                        }
                        onChange={(e) =>
                          updateBreaks(day, "start", e.target.value)
                        }
                        style={{ width: 160 }}
                      />
                      <input
                        className="emp-input"
                        type="time"
                        value={
                          formState.breaks[day]?.[0]?.end || ""
                        }
                        onChange={(e) =>
                          updateBreaks(day, "end", e.target.value)
                        }
                        style={{ width: 160 }}
                      />
                      {breakHourErrors[day] && (
                        <span style={{ color: "#b91c1c" }}>
                          {breakHourErrors[day]}
                        </span>
                      )}
                    </div>
                  ))}
                </>
              )}

              {/* Actions bar */}
              <div className="emp-modal-actions">
                <button
                  type="button"
                  className="emp-btn emp-btn-light"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <div className="emp-actions-right">
                  {/* Back/Next only when editing (multi-step) */}
                  {editingId && step > 0 && (
                    <button
                      type="button"
                      className="emp-btn emp-btn-light"
                      onClick={() => setStep((s) => s - 1)}
                    >
                      Back
                    </button>
                  )}
                  {editingId && step < 3 && (
                    <button
                      type="button"
                      className="emp-btn emp-btn-primary"
                      onClick={() => setStep((s) => s + 1)}
                    >
                      Next
                    </button>
                  )}

                  {/* Save: always shown on Add; only at last step on Edit */}
                  {(!editingId || step === 3) && (
                    <button
                      type="submit"
                      className="emp-btn emp-btn-primary"
                      disabled={loading}
                    >
                      {loading ? "Saving..." : "Save"}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Employees;
